/**
 * EntraAdapter - Microsoft Entra ID Authentication Adapter
 *
 * This adapter implements the AuthAdapter interface for Microsoft Entra ID
 * (formerly Azure Active Directory) using the MSAL.js library.
 *
 * Features:
 * - Popup and redirect authentication flows
 * - Silent token acquisition with automatic refresh
 * - Role extraction from ID token claims
 * - Session management
 */

import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  type AccountInfo,
  type AuthenticationResult,
  type PopupRequest,
  type RedirectRequest,
  type SilentRequest,
} from '@azure/msal-browser';
import {
  type AuthAdapter,
  type AuthUser,
  type LoginOptions,
  AuthError,
} from './AuthAdapter';
import { msalConfig, loginScopes, silentTokenScopes } from '../msalConfig';

/**
 * Extended ID token claims interface for Entra ID tokens
 * Includes standard OIDC claims plus custom role claims
 */
interface EntraIdTokenClaims {
  aud?: string;
  iss?: string;
  iat?: number;
  nbf?: number;
  exp?: number;
  name?: string;
  preferred_username?: string;
  oid?: string;
  sub?: string;
  tid?: string;
  email?: string;
  // Role claims - can be a string or array depending on configuration
  roles?: string[];
  // Alternative role claim locations
  wids?: string[]; // Directory role template IDs
  groups?: string[];
}

/**
 * EntraAdapter - Microsoft Entra ID Authentication Implementation
 *
 * Implements the AuthAdapter interface using MSAL.js for authentication
 * with Microsoft Entra ID (Azure AD).
 */
export class EntraAdapter implements AuthAdapter {
  public readonly providerType = 'entra' as const;

  private msalInstance: PublicClientApplication;
  private initialized = false;
  private initializationPromise: Promise<void> | null = null;

  constructor() {
    this.msalInstance = new PublicClientApplication(msalConfig);
  }

  /**
   * Initialize the MSAL instance
   * Must be called before any other operations
   */
  private async ensureInitialized(): Promise<void> {
    if (this.initialized) {
      return;
    }

    if (!this.initializationPromise) {
      this.initializationPromise = this.msalInstance.initialize().then(() => {
        this.initialized = true;
      });
    }

    await this.initializationPromise;
  }

  /**
   * Get the MSAL instance (for MsalProvider)
   * Ensures initialization before returning
   */
  async getMsalInstance(): Promise<PublicClientApplication> {
    await this.ensureInitialized();
    return this.msalInstance;
  }

  /**
   * Get the currently active account from MSAL
   */
  private getActiveAccount(): AccountInfo | null {
    // First check if there's an active account set
    const activeAccount = this.msalInstance.getActiveAccount();
    if (activeAccount) {
      return activeAccount;
    }

    // Fall back to first account in cache
    const accounts = this.msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      const account = accounts[0];
      if (account) {
        this.msalInstance.setActiveAccount(account);
        return account;
      }
    }

    return null;
  }

  /**
   * Initiates the login flow
   * @param options - Optional configuration for login behavior
   */
  async login(options?: LoginOptions): Promise<void> {
    await this.ensureInitialized();

    const request: PopupRequest | RedirectRequest = {
      ...loginScopes,
      ...(options?.scopes && { scopes: options.scopes }),
      ...(options?.loginHint && { loginHint: options.loginHint }),
    };

    try {
      const interaction = options?.interaction ?? 'popup';

      if (interaction === 'redirect') {
        // Redirect flow - will navigate away from the page
        await this.msalInstance.loginRedirect(request as RedirectRequest);
      } else {
        // Popup flow - stays on the same page
        const response = await this.msalInstance.loginPopup(
          request as PopupRequest
        );
        this.handleAuthenticationResult(response);
      }
    } catch (error) {
      throw this.createAuthError('Login failed', 'LOGIN_FAILED', error);
    }
  }

  /**
   * Signs out the current user
   */
  async logout(): Promise<void> {
    await this.ensureInitialized();

    const account = this.getActiveAccount();

    try {
      if (account) {
        await this.msalInstance.logoutPopup({
          account,
          postLogoutRedirectUri: msalConfig.auth.postLogoutRedirectUri,
        });
      }
    } catch (error) {
      throw this.createAuthError('Logout failed', 'LOGOUT_FAILED', error);
    }
  }

  /**
   * Retrieves the currently authenticated user
   */
  async getUser(): Promise<AuthUser | null> {
    await this.ensureInitialized();

    const account = this.getActiveAccount();
    if (!account) {
      return null;
    }

    // Try to get fresh tokens silently
    const tokenResponse = await this.acquireTokenSilentInternal(account);

    return this.mapAccountToAuthUser(account, tokenResponse);
  }

  /**
   * Retrieves roles from the ID token claims
   */
  async getRoles(): Promise<string[]> {
    await this.ensureInitialized();

    const account = this.getActiveAccount();
    if (!account) {
      return [];
    }

    const claims = account.idTokenClaims as EntraIdTokenClaims | undefined;
    if (!claims) {
      return [];
    }

    // Primary source: roles claim (configured in Azure App Registration)
    if (claims.roles && Array.isArray(claims.roles)) {
      return claims.roles;
    }

    // Fallback: Check for directory role IDs (wids claim)
    // Note: These are GUIDs, not friendly names
    if (claims.wids && Array.isArray(claims.wids)) {
      // Map well-known directory role IDs to names if needed
      return claims.wids;
    }

    // Fallback: Check for group memberships
    if (claims.groups && Array.isArray(claims.groups)) {
      return claims.groups;
    }

    return [];
  }

  /**
   * Checks if a user is currently authenticated
   */
  isAuthenticated(): boolean {
    // Note: This is a synchronous check, may not reflect token validity
    return this.getActiveAccount() !== null;
  }

  /**
   * Silently acquires a new access token
   */
  async acquireTokenSilent(): Promise<string | null> {
    await this.ensureInitialized();

    const account = this.getActiveAccount();
    if (!account) {
      return null;
    }

    const response = await this.acquireTokenSilentInternal(account);
    return response?.accessToken ?? null;
  }

  /**
   * Handles redirect callback after authentication redirect flow
   */
  async handleRedirectCallback(): Promise<void> {
    await this.ensureInitialized();

    try {
      const response = await this.msalInstance.handleRedirectPromise();
      if (response) {
        this.handleAuthenticationResult(response);
      }
    } catch (error) {
      throw this.createAuthError(
        'Failed to handle redirect callback',
        'REDIRECT_CALLBACK_FAILED',
        error
      );
    }
  }

  /**
   * Internal method to acquire tokens silently
   */
  private async acquireTokenSilentInternal(
    account: AccountInfo
  ): Promise<AuthenticationResult | null> {
    const request: SilentRequest = {
      ...silentTokenScopes,
      account,
    };

    try {
      return await this.msalInstance.acquireTokenSilent(request);
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // Token expired or requires interaction - return null
        // The calling code should handle re-authentication
        console.warn(
          '[EntraAdapter] Silent token acquisition failed, interaction required'
        );
        return null;
      }
      console.error('[EntraAdapter] Silent token acquisition error:', error);
      return null;
    }
  }

  /**
   * Handles the authentication result from login
   */
  private handleAuthenticationResult(response: AuthenticationResult): void {
    if (response.account) {
      this.msalInstance.setActiveAccount(response.account);
    }
  }

  /**
   * Maps MSAL AccountInfo to our AuthUser interface
   */
  private mapAccountToAuthUser(
    account: AccountInfo,
    tokenResponse?: AuthenticationResult | null
  ): AuthUser {
    const claims = account.idTokenClaims as EntraIdTokenClaims | undefined;

    return {
      id: account.localAccountId,
      email:
        claims?.email ?? claims?.preferred_username ?? account.username ?? '',
      displayName: claims?.name ?? account.name ?? '',
      photoUrl: undefined, // Not available from Entra ID claims directly
      idToken: account.idToken,
      accessToken: tokenResponse?.accessToken,
      tokenExpiry: tokenResponse?.expiresOn?.getTime(),
    };
  }

  /**
   * Creates a standardized AuthError
   */
  private createAuthError(
    message: string,
    code: string,
    originalError?: unknown
  ): AuthError {
    return new AuthError(message, code, this.providerType, originalError);
  }
}

// Export a singleton instance for convenience
export const entraAdapter = new EntraAdapter();
